import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowRight, FileDown, Mail, RefreshCcw, ShieldCheck } from 'lucide-react'
import { authorizeGoogle } from '../lib/drive'
import { downloadGmailMessage, GMAIL_SCOPE, GmailTokenExpiredError, listGmailMessages, type GmailMessageRef } from '../lib/gmail'
import { writeBlob, writeManifest } from '../lib/filesystem'
import type { CopyProgressState, CopyRecord, CopyStage } from '../types'
import { CopyProgress } from './CopyProgress'

interface GmailBackupProps {
  clientId: string
  clientReady: boolean
  destination: FileSystemDirectoryHandle | null
  hasActivePass: boolean
  initialQuery?: string
}

const initialProgress: CopyProgressState = { current: 0, total: 0, bytesCopied: 0, bytesTotal: 0, bytesKnown: false, currentName: '', errors: [] }

function emailPath(subject: string, date: Date, id: string) {
  const year = String(date.getFullYear())
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `libregoo/Gmail/${year}/${month}/${subject}__${id}.eml`
}

function downloadManifest(records: CopyRecord[], query: string) {
  const url = URL.createObjectURL(new Blob([JSON.stringify({ herramienta: 'libregoo Directo PWA', origen: 'Gmail', consulta: query || 'todos los mensajes', generado: new Date().toISOString(), archivos: records }, null, 2)], { type: 'application/json' }))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = 'manifiesto-gmail-libregoo.json'
  anchor.click()
  URL.revokeObjectURL(url)
}

export function GmailBackup({ clientId, clientReady, destination, hasActivePass, initialQuery = '' }: GmailBackupProps) {
  const [token, setToken] = useState('')
  const [query, setQuery] = useState(initialQuery)
  const [messages, setMessages] = useState<GmailMessageRef[]>([])
  const [stage, setStage] = useState<CopyStage>('idle')
  const [message, setMessage] = useState('Copia todos tus correos o limita el backup usando la búsqueda nativa de Gmail.')
  const [progress, setProgress] = useState<CopyProgressState>(initialProgress)
  const [records, setRecords] = useState<CopyRecord[]>([])
  const copyLock = useRef(false)
  const isCopying = stage === 'copying'
  const estimatedLabel = useMemo(() => messages.length ? `${messages.length.toLocaleString('es-ES')} mensajes encontrados` : 'Aún sin inventario', [messages.length])

  useEffect(() => {
    if (copyLock.current) return
    setQuery(initialQuery)
    setMessages([])
    setRecords([])
    setStage('idle')
    setMessage(initialQuery ? `Se aplicó la selección guardada: ${initialQuery}. Analiza Gmail antes de copiar.` : 'Copia todos tus correos o limita el backup usando la búsqueda nativa de Gmail.')
  }, [initialQuery])

  const connect = async () => {
    if (copyLock.current) return
    try {
      setMessage('Abriendo el consentimiento oficial de Google para Gmail…')
      const accessToken = await authorizeGoogle(clientId, GMAIL_SCOPE, 'Gmail')
      setToken(accessToken)
      setStage('idle')
      setMessage('Gmail conectado con permiso de sólo lectura. Ahora puedes analizar el alcance del backup.')
    } catch (error) {
      setStage('error')
      setMessage(error instanceof Error ? error.message : 'No fue posible conectar Gmail.')
    }
  }

  const scan = async () => {
    if (copyLock.current || !token) return
    try {
      setStage('scanning')
      setMessage('Leyendo sólo el inventario de Gmail para calcular el alcance…')
      const found = await listGmailMessages(token, query, (count) => setMessage(`Analizando Gmail: ${count.toLocaleString('es-ES')} mensajes encontrados…`))
      setMessages(found)
      setStage('ready')
      setMessage(found.length ? 'Inventario listo. Los adjuntos viajan dentro de cada mensaje .eml.' : 'No hay mensajes que coincidan con esta consulta.')
    } catch (error) {
      setStage('error')
      setMessage(error instanceof Error ? error.message : 'No se pudo analizar Gmail.')
    }
  }

  const updateQuery = (nextQuery: string) => {
    setQuery(nextQuery)
    if (messages.length) {
      setMessages([])
      setRecords([])
      setStage('idle')
      setMessage('La consulta cambió. Analiza Gmail de nuevo antes de copiar mensajes.')
    }
  }

  const copy = async () => {
    if (copyLock.current) return
    if (!hasActivePass) { window.location.assign('/copia'); return }
    if (!token) { setMessage('Conecta Gmail antes de iniciar la copia.'); return }
    if (!destination) { setMessage('Elige una carpeta local arriba antes de iniciar la copia de Gmail.'); return }
    if (!messages.length) { setMessage('Analiza Gmail antes de iniciar una copia.'); return }

    const runMessages = [...messages]
    const runDestination = destination
    const runToken = token
    const runQuery = query
    const copyRecords: CopyRecord[] = []
    let bytesCopied = 0
    copyLock.current = true
    setStage('copying')
    setProgress({ current: 0, total: runMessages.length, bytesCopied: 0, bytesTotal: 0, bytesKnown: false, currentName: '', errors: [] })
    setMessage('Los mensajes se descargan directamente de Gmail a tu carpeta. Mantén esta pestaña abierta.')

    const persist = async (status: 'complete' | 'partial' | 'interrupted') => {
      try {
        await writeManifest(runDestination, copyRecords, { origen: 'Gmail', consulta: runQuery || 'todos los mensajes', destino: runDestination.name, estado: status })
        return true
      } catch (error) {
        setProgress((current) => ({ ...current, errors: [...current.errors, error instanceof Error ? error.message : 'No se pudo guardar el manifiesto de Gmail.'] }))
        return false
      }
    }

    try {
      for (let index = 0; index < runMessages.length; index += 1) {
        const reference = runMessages[index]
        try {
          setProgress((current) => ({ ...current, currentName: `Mensaje ${index + 1} de ${runMessages.length}` }))
          const mail = await downloadGmailMessage(runToken, reference.id)
          const outputPath = await writeBlob(runDestination, emailPath(mail.subject, mail.date, mail.id), mail.blob)
          bytesCopied += mail.size
          copyRecords.push({ id: mail.id, sourceName: mail.subject, outputPath, bytes: mail.size, status: 'copied', copiedAt: new Date().toISOString() })
        } catch (error) {
          const detail = error instanceof Error ? error.message : 'Error desconocido.'
          copyRecords.push({ id: reference.id, sourceName: reference.id, outputPath: '', bytes: 0, status: 'failed', copiedAt: new Date().toISOString(), error: detail })
          if (error instanceof GmailTokenExpiredError) {
            setToken('')
            const errors = copyRecords.filter((record) => record.status === 'failed').map((record) => `${record.sourceName}: ${record.error}`)
            setProgress((current) => ({ ...current, current: index + 1, bytesCopied, errors }))
            const manifestSaved = await persist('interrupted')
            setRecords(copyRecords)
            setStage(manifestSaved ? 'partial' : 'error')
            setMessage(manifestSaved ? 'Gmail pidió renovar el permiso. Los mensajes ya copiados y el manifiesto parcial permanecen en tu carpeta.' : 'Gmail pidió renovar el permiso y no se pudo guardar el manifiesto.')
            return
          }
        }
        const errors = copyRecords.filter((record) => record.status === 'failed').map((record) => `${record.sourceName}: ${record.error}`)
        setProgress((current) => ({ ...current, current: index + 1, bytesCopied, errors }))
      }

      const errors = copyRecords.filter((record) => record.status === 'failed')
      const manifestSaved = await persist(errors.length ? 'partial' : 'complete')
      setRecords(copyRecords)
      if (!manifestSaved) {
        setStage('error')
        setMessage('Los mensajes se guardaron, pero no se pudo escribir el manifiesto local.')
      } else if (errors.length) {
        setStage('partial')
        setMessage(`El backup de Gmail terminó con ${errors.length} incidencia(s). El manifiesto local contiene el detalle.`)
      } else {
        setStage('complete')
        setMessage('Backup de Gmail terminado. Cada correo y adjunto ya está en tu carpeta local.')
      }
    } finally {
      copyLock.current = false
    }
  }

  return <section className="gmail-backup" id="gmail">
    <div className="gmail-heading"><div><span className="kicker">GMAIL · BACKUP DIRECTO</span><h2>Guarda tus correos y adjuntos.</h2><p>Se descargan mensajes completos en formato .eml: puedes abrirlos en clientes de correo y los adjuntos permanecen dentro del mensaje.</p></div><div className="source-status"><span className={token ? 'status-dot is-connected' : 'status-dot'} /><strong>{token ? 'Gmail conectado' : 'Gmail sin conectar'}</strong></div></div>
    <div className="gmail-controls"><label><span>Qué correos quieres incluir</span><input value={query} disabled={isCopying} onChange={(event) => updateQuery(event.target.value)} placeholder="Todo Gmail (o: older_than:2y has:attachment)" /><small>Usa la búsqueda nativa de Gmail: por ejemplo, <code>older_than:2y has:attachment</code>.</small></label><div className="gmail-action-stack"><button className="button button-secondary" type="button" disabled={!clientReady || isCopying || token !== ''} onClick={connect}><Mail size={17} /> {token ? 'Gmail conectado' : 'Conectar Gmail'}</button><button className="button button-secondary" type="button" disabled={!token || isCopying || stage === 'scanning'} onClick={scan}>{stage === 'scanning' ? <RefreshCcw className="spin" size={17} /> : <FileDown size={17} />}{stage === 'scanning' ? 'Analizando…' : 'Analizar Gmail'}</button></div></div>
    <div className="gmail-review"><div><strong>{estimatedLabel}</strong><p aria-live="polite">{message}</p></div><div><span><ShieldCheck size={15} /> {hasActivePass ? 'Pase activo · adjuntos incluidos' : 'Analiza gratis · paga sólo al copiar'}</span><button className="button button-primary" type="button" disabled={!token || !destination || !messages.length || isCopying || stage === 'scanning'} onClick={copy}>{hasActivePass ? 'Copiar Gmail' : 'Desbloquear por 4,90 €'} <ArrowRight size={17} /></button></div></div>
    <CopyProgress stage={stage} progress={progress} onDownloadManifest={() => downloadManifest(records, query)} />
  </section>
}
